import { FileText, Mail, Webhook } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { BroadcastChannel } from "./use-broadcasts";

/**
 * Slack/Teams keep their real brand logos — the exact assets SCIO3's integrations
 * use — so a destination is recognisable at a glance; the credential-free channels
 * (email/pdf/webhook) are tinted Lucide glyphs, same recipe as SCIO3's ChannelIcon.
 */
const BRAND_CDN: Record<string, string> = {
  slack: "slack",
  teams: "microsoftTeam",
};

export const CHANNEL_LABELS: Record<string, string> = {
  slack: "Slack",
  teams: "Microsoft Teams",
  email: "Email",
  pdf: "PDF",
  webhook: "Webhook",
};

/** Canonical display order for channel chips, everywhere they appear. */
export const CHANNEL_ORDER = ["slack", "teams", "email", "pdf", "webhook"] as const;

export function ChannelIcon({
  type,
  className,
}: {
  type: string;
  className?: string;
}): JSX.Element | null {
  const cdn = BRAND_CDN[type];
  if (cdn) {
    return (
      <img
        src={`https://cdn.optisigns.com/scio/images/${cdn}.png`}
        alt=""
        aria-hidden
        className={cn("object-contain", className)}
      />
    );
  }
  if (type === "email") {
    return (
      <Mail aria-hidden className={cn("text-sky-600 dark:text-sky-400", className)} />
    );
  }
  if (type === "pdf") {
    return (
      <FileText
        aria-hidden
        className={cn("text-rose-600 dark:text-rose-400", className)}
      />
    );
  }
  if (type === "webhook") {
    return (
      <Webhook
        aria-hidden
        className={cn("text-violet-600 dark:text-violet-400", className)}
      />
    );
  }
  return null;
}

/** One bordered 26px icon chip with a tooltip naming the channel AND its target. */
export function ChannelChip({
  type,
  tip,
  className,
}: {
  type: string;
  tip: string;
  className?: string;
}): JSX.Element {
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        {/* testid because the chip is icon-only — e2e can't assert channel text anymore */}
        <span
          data-testid={`broadcast-channel-chip-${type}`}
          className={cn(
            "flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border bg-background",
            className,
          )}
        >
          <ChannelIcon type={type} className="h-3.5 w-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{tip}</TooltipContent>
    </Tooltip>
  );
}

/** Host of a URL for compact webhook tooltips; falls back to the raw value. */
function hostOf(url: unknown): string | null {
  if (typeof url !== "string" || !url) return null;
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** The tooltip line for a channel type, from that type's channel rows. */
function channelTip(type: string, rows: BroadcastChannel[]): string {
  const label = CHANNEL_LABELS[type] ?? type;
  if (type === "slack") {
    if (rows.length > 1) return `${label} ×${rows.length}`;
    const name = rows[0]?.config?.channelName;
    return typeof name === "string" && name ? `${label} — #${name}` : label;
  }
  if (type === "email") {
    const recipients = rows[0]?.config?.recipients;
    return Array.isArray(recipients) && recipients.length > 0
      ? `${label} — ${recipients.length} recipient${recipients.length > 1 ? "s" : ""}`
      : label;
  }
  if (type === "pdf") return `${label} — saved to Snapshots`;
  if (type === "webhook") {
    const host = hostOf(rows[0]?.config?.url);
    return host ? `${label} — ${host}` : label;
  }
  return label;
}

const MAX_CHIPS = 3;

/**
 * A broadcast's destinations as brand chips — one per channel TYPE in canonical
 * order, at most three, then a "+N" overflow chip whose tooltip lists the rest.
 */
export function ChannelChips({
  channels,
}: {
  channels: BroadcastChannel[];
}): JSX.Element {
  const byType = new Map<string, BroadcastChannel[]>();
  for (const ch of channels) {
    byType.set(ch.type, [...(byType.get(ch.type) ?? []), ch]);
  }
  const ordered = [
    ...CHANNEL_ORDER.filter((t) => byType.has(t)),
    ...[...byType.keys()].filter(
      (t) => !(CHANNEL_ORDER as readonly string[]).includes(t),
    ),
  ];
  const visible = ordered.slice(0, MAX_CHIPS);
  const overflow = ordered.slice(MAX_CHIPS);

  return (
    <div className="flex items-center gap-1.5">
      {visible.map((type) => (
        <ChannelChip
          key={type}
          type={type}
          tip={channelTip(type, byType.get(type) ?? [])}
        />
      ))}
      {overflow.length > 0 && (
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <span className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-md border border-border bg-background px-1 text-[10px] font-semibold text-muted-foreground">
              +{overflow.length}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            {overflow.map((t) => CHANNEL_LABELS[t] ?? t).join(" · ")}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
